import { Box, Typography } from '@mui/material';
import { useCallback, useEffect, useState } from 'react';

import { TreeItem } from '@mui/x-tree-view';
import { formatDate, queryParams } from '@shared/commonFunctions';
import { dateFormatWithTime } from '@shared/constants/formats';
import { Loader } from '@shared/enhancers/withLoader';
import { NContact } from '@modules/ContactManager/components/Tabs/components/Contact/validation';
import { LoaderWrapper, TreeViewComponentWrapper, TreeViewItemWrapper } from '@styles/CommonStyles';
import { FlexColumn, TreeViewWrapper } from '@styles/StyledDiv';

import { LoaderBackdrop } from '@shared/components/LoaderBackdrop';
import { DealProduct, Deductible, Option, Plan, Plans, ProductPlan, Provider, Rate, Surcharge } from '../interfaces';
import { getPlan } from '../queries';
import DeductibleTreeItem from './DeductibleTreeItem';
import { SelectedRetails } from './EditProductModal';
import OptionsTreeItem from './OptionsTreeItem';

const getListOfExpanded = (
  data: Plans,
  setProductRules: (productRules: ProductPlan[]) => void,
  selectedRetails: SelectedRetails
): string[] => {
  const expandList: string[] = [];
  const productRules: ProductPlan[] = [];

  data?.productPlans?.forEach((product: ProductPlan) => {
    expandList.push(product.productTypeId);
    productRules.push(product);

    product.providers?.forEach((provider: Provider) => {
      expandList.push(product.productTypeId + provider.providerId);
      provider.plans.forEach((plan: Plan) => {
        expandList.push(product.productTypeId + provider.providerId + plan.id + plan.name);
        plan.rates.forEach((rate: Rate) => {
          rate.deductibles.map((option) => {
            const { dealsProductsMap } = selectedRetails;
            const currentDealProduct = dealsProductsMap.get(product.productTypeId);
            const currentDealProductId = (currentDealProduct?.rate?.rateId || '') + currentDealProduct?.deductible.id;
            const localId = rate.rateId + option.deductibleId;
            const isSelected = localId === currentDealProductId;
            if (isSelected) {
              expandList.push(provider.providerId + plan.id + plan.name + rate.rateId);
            }
          });
        });
      });
    });
  });
  setProductRules(productRules);

  return expandList;
};

interface IProductsTree {
  setSelectedRetails: (retails: SelectedRetails) => void;
  selectedRetails: SelectedRetails;
  setProductRules: (productRules: ProductPlan[]) => void;
  getTreeUrl?: string;
  dealProducts?: DealProduct[];
  type?: string;
  contactAddress?: NContact.Address;
  refresh: number;
  preventDealSave: boolean;
  forceVendorRepull?: boolean | null | undefined;
}

export const ProductsTree = ({
  setSelectedRetails,
  selectedRetails,
  selectedRetails: { optionSelected, surchargeSelected },
  setProductRules,
  getTreeUrl,
  dealProducts,
  type,
  contactAddress,
  refresh,
  forceVendorRepull
}: IProductsTree) => {
  const [isDeductiblesSelected, setIsDeductiblesSelected] = useState(false);
  const [defaultExpanded, setDefaultExpanded] = useState<string[]>([]);
  const [plans, setPlans] = useState<ProductPlan[]>([]);
  const [data, setData] = useState<Plans>();
  const [loading, setLoading] = useState(false);
  const [isNewVersion, setIsNewVersion] = useState(false);
  
  const params = queryParams();
  const dealId = params.dealId;

  const getData = useCallback(
    async (isNewVersion: boolean, forceVendorRepull?: boolean | null | undefined) => {
      setLoading(true);
      setIsNewVersion(isNewVersion);
      const planData = await getPlan(getTreeUrl || '', dealId, isNewVersion, contactAddress?.state, forceVendorRepull);
      setData(planData);
      setLoading(false);
    },
    [getTreeUrl, dealId]
  );

  const refreshProducts = useCallback(() => {
    getData(isNewVersion, forceVendorRepull);
  }, [forceVendorRepull, isNewVersion]);

  useEffect(() => {
    refresh > 0 && refreshProducts();
  }, [refresh]);

  useEffect(() => {
    getData(false, forceVendorRepull);
  }, [forceVendorRepull]);

  useEffect(() => {
    if (data?.productPlans) {
      setDefaultExpanded(getListOfExpanded(data, setProductRules, selectedRetails));
      const sortedData = [...data.productPlans].sort((a: ProductPlan) => (a.isContracted ? -1 : 1));
      setPlans(sortedData);
    }
  }, [data, selectedRetails]);

  const renderOptionsList = ({
    provider,
    plan,
    product,
    rate,
  }: {
    rate: Rate;
    product: ProductPlan;
    provider: Provider;
    plan: Plan;
  }): JSX.Element[] => {
    const mainProps = {
      provider,
      plan,
      product,
      rate,
      selectedRetails,
      setSelectedRetails,
      setIsDeductiblesSelected,
      isDeductiblesSelected,
      dealProducts,
      type,
    };
    const deductibles = rate.deductibles.map((option: Deductible) => (
      <DeductibleTreeItem
        key={provider.providerId + plan.planId + rate.rateId + option.id}
        {...{
          ...mainProps,
          option,
        }}
      />
    ));

    const options = rate.options.map((option: Option) => {
      return (
        <OptionsTreeItem
          key={provider.providerId + plan.planId + rate.rateId + option.optionId}
          {...{
            ...mainProps,
            option,
            optionList: optionSelected,
            isSurcharge: false,
          }}
        />
      );
    });

    const surcharges = rate.surcharges.map((option: Surcharge) => (
      <OptionsTreeItem
        key={provider.providerId + plan.planId + rate.rateId + option.surchargeId}
        {...{
          ...mainProps,
          option,
          optionList: surchargeSelected,
          isSurcharge: true,
        }}
      />
    ));
    return [...deductibles, ...surcharges, ...options];
  };

  if (loading || (data?.productPlans.length && !defaultExpanded.length)) {
    return <LoaderBackdrop open />;
  }

  // const onGetPlansClick = () => {
  //   getData(true);
  // };

  return (
    <TreeViewWrapper>
      <Box sx={{ display: 'flex', marginBottom: '10px' }}>
        {/* <Button
            variant="contained"
            disabled={preventDealSave}
            onClick={onGetPlansClick}
          >
            Get Plans
          </Button> */}
        <FlexColumn>
          <Typography sx={{ fontWeight: 600, marginBottom: '10px' }}>
            Plan Date: {formatDate(data?.productPlanDate, { format: dateFormatWithTime })}
          </Typography>
        </FlexColumn>
      </Box>

      <TreeViewComponentWrapper isLoading={loading}>
        {loading && (
          <LoaderWrapper>
            <Loader />
          </LoaderWrapper>
        )}
        <TreeViewItemWrapper defaultExpandedItems={defaultExpanded}>
          {plans?.map((product: ProductPlan) =>
            product.isContracted ? (
              <Typography
                key={product?.productTypeId}
                padding="2px 23px"
              >
                {product?.productName} is Contracted! Must be voided before change.
              </Typography>
            ) : (
              <TreeItem
                key={product.productTypeId}
                itemId={product.productTypeId}
                label={product.productName}
              >
                {product.providers.map((provider: Provider) => (
                  <TreeItem
                    key={`treeitem2-${product.productTypeId}-${provider.providerId}`}
                    itemId={product.productTypeId + provider.providerId}
                    label={provider.providerName}
                  >
                    {provider.plans.map((plan: Plan) => (
                      <TreeItem
                        key={`treeitem3-${product.productTypeId} - ${provider.providerId}-${plan.id}-${plan.name}`}
                        itemId={product.productTypeId + provider.providerId + plan.id + plan.name}
                        label={plan.name}
                      >
                        {plan.rates.map((rate: Rate) => (
                          <TreeItem
                            key={`treeitem4-${provider.providerId}-${plan.id}-${rate.rateId}-${rate.rateDescription}`}
                            itemId={provider.providerId + plan.id + plan.name + rate.rateId}
                            label={rate.rateDescription}
                          >
                            {renderOptionsList({ provider, plan, product, rate })}
                          </TreeItem>
                        ))}
                      </TreeItem>
                    ))}
                  </TreeItem>
                ))}
              </TreeItem>
            )
          )}
        </TreeViewItemWrapper>
      </TreeViewComponentWrapper>
    </TreeViewWrapper>
  );
};
